import { quickAlert } from '../utils/alerts';

/**
 * OCR onboarding (frontend): nahranie fotky → polling spracovania → editovateľný
 * review draftu (zvýraznené neisté polia) → potvrdenie zápisu. Backend nikdy
 * nezapíše menu bez tohto confirm kroku.
 *
 * Napája sa na endpointy MenuOcrController (store/status/confirm) cez data-*
 * atribúty na #menu-ocr. Modal je vlastný overlay (nezávislý od magnificPopup).
 */

type Warning = { path: string; code: string };
type OcrItem = { name?: string | null; price?: number | null; allergies?: string[] } & Record<string, unknown>;
type OcrDay = { name?: string | null; day_of_week?: number | null; date?: string | null; items?: OcrItem[] } & Record<string, unknown>;
type OcrDraft = { days?: OcrDay[] } & Record<string, unknown>;
type OcrImport = {
    id: number;
    status: 'pending' | 'processing' | 'needs_review' | 'failed' | 'imported';
    attempts: number;
    max_attempts: number;
    draft: OcrDraft | null;
    warnings: Warning[] | null;
    error: string | null;
};

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_TRIES = 60;

export function initMenuOcr(selector = '#menu-ocr'): void {
    const root = document.querySelector<HTMLElement>(selector);
    if (!root) {
        return;
    }

    const storeUrl = root.dataset.storeUrl ?? '';
    const statusUrlTpl = root.dataset.statusUrl ?? '';
    const confirmUrlTpl = root.dataset.confirmUrl ?? '';
    const menuUrl = root.dataset.menuUrl ?? '';

    const fileInput = root.querySelector<HTMLInputElement>('#ocr-file');
    const statusBox = root.querySelector<HTMLElement>('#ocr-status');
    const statusText = root.querySelector<HTMLElement>('#ocr-status-text');
    const modal = root.querySelector<HTMLElement>('#ocr-review-modal');
    const reviewBody = root.querySelector<HTMLElement>('#ocr-review-body');
    const reviewBanner = root.querySelector<HTMLElement>('#ocr-review-banner');
    const confirmBtn = root.querySelector<HTMLButtonElement>('#ocr-confirm');

    if (!fileInput || !statusBox || !statusText || !modal || !reviewBody || !confirmBtn) {
        return;
    }

    let currentImportId: number | null = null;
    let lastDraft: OcrDraft = {};

    // „Nahrať fotku" – otvorí výber súboru.
    root.querySelectorAll('.js-ocr-pick').forEach((el) => {
        el.addEventListener('click', () => fileInput.click());
    });

    // „Zo šablóny" – využije existujúci flow pridania týždňového menu.
    root.querySelectorAll('.js-ocr-template').forEach((el) => {
        el.addEventListener('click', () => {
            document.querySelector<HTMLElement>('#main-add-category')?.click();
        });
    });

    // „Zadať ručne" – fallback: zatvor stav a otvor pridanie týždňa.
    root.querySelectorAll('.js-ocr-manual').forEach((el) => {
        el.addEventListener('click', () => {
            hide(statusBox!);
            document.querySelector<HTMLElement>('#main-add-category')?.click();
        });
    });

    root.querySelectorAll('.js-ocr-close').forEach((el) => {
        el.addEventListener('click', () => hide(modal!));
    });

    fileInput.addEventListener('change', () => {
        const file = fileInput.files?.[0];
        if (file) {
            void upload(file);
        }
        fileInput.value = '';
    });

    confirmBtn.addEventListener('click', () => void confirmDraft());

    async function upload(file: File): Promise<void> {
        showStatus('Nahrávam fotku…');

        const form = new FormData();
        form.append('image', file);

        try {
            const res = await request(storeUrl, { method: 'POST', body: form });
            const data = await res.json();

            if (!res.ok || !data?.success) {
                showError(data?.message ?? 'Nahranie zlyhalo.', data?.import ?? null);
                return;
            }

            currentImportId = data.import.id;
            showStatus('Spracúvam menu…');
            void poll(0);
        } catch {
            showError('Nahranie zlyhalo. Skús to znova.', null);
        }
    }

    async function poll(tries: number): Promise<void> {
        if (currentImportId === null) {
            return;
        }

        if (tries >= POLL_MAX_TRIES) {
            showError('Spracovanie trvá príliš dlho. Skús to znova.', null);
            return;
        }

        try {
            const res = await request(statusUrlTpl.replace('MENU_OCR_ID', String(currentImportId)));
            const data = await res.json();
            const imp: OcrImport | undefined = data?.import;

            if (!imp) {
                showError('Stav sa nepodarilo načítať.', null);
                return;
            }

            if (imp.status === 'pending' || imp.status === 'processing') {
                window.setTimeout(() => void poll(tries + 1), POLL_INTERVAL_MS);
                return;
            }

            if (imp.status === 'needs_review') {
                hide(statusBox!);
                openReview(imp);
                return;
            }

            if (imp.status === 'imported') {
                window.location.href = menuUrl;
                return;
            }

            showError(imp.error ?? 'Fotku sa nepodarilo spoľahlivo prečítať.', imp);
        } catch {
            showError('Stav sa nepodarilo načítať.', null);
        }
    }

    function openReview(imp: OcrImport): void {
        const draft = imp.draft ?? {};
        lastDraft = draft;
        const warnings = imp.warnings ?? [];
        const warnPaths = new Set(warnings.map((w) => w.path));

        reviewBody!.innerHTML = '';
        renderBanner(warnings);

        const days = Array.isArray(draft.days) ? draft.days : [];
        days.forEach((day, dayIndex) => {
            reviewBody!.appendChild(buildDay(day, dayIndex, warnPaths));
        });

        show(modal!);
    }

    function renderBanner(warnings: Warning[]): void {
        const prefilled = warnings.some((w) => w.code === 'week_prefilled');
        if (prefilled) {
            reviewBanner!.textContent = 'Týždeň sme predvyplnili podľa aktuálneho dátumu – over ho prosím.';
            show(reviewBanner!);
        } else {
            hide(reviewBanner!);
        }
    }

    function buildDay(day: OcrDay, dayIndex: number, warnPaths: Set<string>): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'ocr-day';
        wrap.dataset.dayIndex = String(dayIndex);

        const head = document.createElement('div');
        head.className = 'ocr-day__head';
        head.appendChild(labeledInput('Deň', 'text', day.name ?? '', 'day-name'));
        head.appendChild(labeledInput('Dátum', 'date', day.date ?? '', 'day-date'));
        wrap.appendChild(head);

        const items = Array.isArray(day.items) ? day.items : [];
        items.forEach((item, itemIndex) => {
            wrap.appendChild(buildItem(item, dayIndex, itemIndex, warnPaths));
        });

        return wrap;
    }

    function buildItem(item: OcrItem, dayIndex: number, itemIndex: number, warnPaths: Set<string>): HTMLElement {
        const row = document.createElement('div');
        row.className = 'ocr-item';
        row.dataset.itemIndex = String(itemIndex);

        const base = `days.${dayIndex}.items.${itemIndex}`;
        const allergies = Array.isArray(item.allergies) ? item.allergies.join(', ') : '';

        row.appendChild(field('Jedlo', textInput(item.name ?? '', 'item-name', warnPaths.has(`${base}.name`))));
        row.appendChild(field('Cena', textInput(item.price != null ? String(item.price) : '', 'item-price', warnPaths.has(`${base}.price`))));
        row.appendChild(field('Alergény', textInput(allergies, 'item-allergies', false)));

        return row;
    }

    async function confirmDraft(): Promise<void> {
        if (currentImportId === null) {
            return;
        }

        const draft = collectDraft();
        confirmBtn!.disabled = true;

        try {
            const res = await request(confirmUrlTpl.replace('MENU_OCR_ID', String(currentImportId)), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ draft }),
            });
            const data = await res.json();

            if (!res.ok || !data?.success) {
                quickAlert(data?.message ?? 'Uloženie zlyhalo.', 'error');
                return;
            }

            quickAlert('Menu bolo vytvorené.', 'success');
            window.location.href = menuUrl;
        } catch {
            quickAlert('Uloženie zlyhalo.', 'error');
        } finally {
            confirmBtn!.disabled = false;
        }
    }

    /**
     * Vyzbiera upravené hodnoty späť do pôvodného draftu (ostatné polia – day_of_week,
     * menu_kind, type… – ostávajú zachované pre WeeklyMenuBuilder).
     */
    function collectDraft(): OcrDraft {
        const draft: OcrDraft = structuredCloneSafe(lastDraft);
        const days = Array.isArray(draft.days) ? draft.days : [];

        reviewBody!.querySelectorAll<HTMLElement>('.ocr-day').forEach((dayEl) => {
            const dayIndex = Number(dayEl.dataset.dayIndex);
            const day = days[dayIndex];
            if (!day) {
                return;
            }

            day.name = val(dayEl, '.day-name') || null;
            day.date = val(dayEl, '.day-date') || null;

            const items = Array.isArray(day.items) ? day.items : [];
            dayEl.querySelectorAll<HTMLElement>('.ocr-item').forEach((itemEl) => {
                const itemIndex = Number(itemEl.dataset.itemIndex);
                const item = items[itemIndex];
                if (!item) {
                    return;
                }

                item.name = val(itemEl, '.item-name') || null;
                const price = val(itemEl, '.item-price').replace(',', '.');
                item.price = price === '' || Number.isNaN(Number(price)) ? null : Number(price);
                item.allergies = val(itemEl, '.item-allergies')
                    .split(',')
                    .map((c) => c.trim())
                    .filter((c) => c !== '');
            });
        });

        return draft;
    }

    // ----- UI helpers -----

    function showStatus(text: string): void {
        statusBox!.classList.remove('is-error');
        statusText!.textContent = text;
        toggle(statusBox!.querySelector('.ocr-status__spinner'), true);
        root!.querySelectorAll('.ocr-retry, .ocr-manual').forEach((el) => hide(el as HTMLElement));
        show(statusBox!);
    }

    function showError(message: string, imp: OcrImport | null): void {
        statusBox!.classList.add('is-error');
        statusText!.textContent = message;
        show(statusBox!);

        const exhausted = imp != null && imp.attempts >= imp.max_attempts;
        root!.querySelectorAll('.ocr-retry').forEach((el) => toggle(el as HTMLElement, !exhausted));
        root!.querySelectorAll('.ocr-manual').forEach((el) => show(el as HTMLElement));
    }
}

// ----- module-scope DOM utils -----

function request(url: string, init: RequestInit = {}): Promise<Response> {
    const token = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? '';

    return fetch(url, {
        ...init,
        headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': token,
            ...(init.headers ?? {}),
        },
    });
}

function labeledInput(label: string, type: string, value: string, cls: string): HTMLElement {
    return field(label, textInput(value, cls, false, type));
}

function field(label: string, input: HTMLElement): HTMLElement {
    const wrap = document.createElement('label');
    wrap.className = 'ocr-field';
    const span = document.createElement('span');
    span.textContent = label;
    wrap.appendChild(span);
    wrap.appendChild(input);
    return wrap;
}

function textInput(value: string, cls: string, warn: boolean, type = 'text'): HTMLInputElement {
    const input = document.createElement('input');
    input.type = type;
    input.className = `with-border ${cls}` + (warn ? ' ocr-warn' : '');
    input.value = value;
    return input;
}

function val(scope: HTMLElement, selector: string): string {
    return (scope.querySelector<HTMLInputElement>(selector)?.value ?? '').trim();
}

function show(el: HTMLElement): void {
    el.classList.remove('hidden');
}

function hide(el: HTMLElement): void {
    el.classList.add('hidden');
}

function toggle(el: Element | null, visible: boolean): void {
    if (el) {
        (el as HTMLElement).classList.toggle('hidden', !visible);
    }
}

function structuredCloneSafe<T>(value: T): T {
    if (typeof structuredClone === 'function') {
        return structuredClone(value);
    }
    return JSON.parse(JSON.stringify(value));
}